/**
 * A device whose media session fails mid-call rejoins the call. It does not
 * hang the call up.
 *
 * The Pixel incident is ROADMAP P0 item 8 under Calling, media and rooms,
 * reported by the owner on 2026-09-06. An Android VRS client was in a call
 * with a web interpreter whose camera was not showing. When the interpreter turned the camera on,
 * the interpreter was "ejected" and the call ended for both of them.
 *
 * The chain, in code:
 * - The SDK (lib-jitsi-meet) raises a failed renegotiation of the bridge
 *   session as the fatal conference error OFFER_ANSWER_FAILED (JitsiConferenceEventManager:
 *   RENEGOTIATION_FAILED, SET_LOCAL/REMOTE_DESCRIPTION_FAILED and
 *   CREATE_ANSWER_FAILED on a non-P2P session). Adding a camera mid-call is a
 *   renegotiation.
 * - On web, conference.js answers OFFER_ANSWER_FAILED, ICE_FAILED, FOCUS_LEFT
 *   and VIDEOBRIDGE_NOT_AVAILABLE by dispatching conferenceWillLeave for the
 *   failed room itself. The queue middleware read that as the person leaving:
 *   call_end to the server, which ends the call for everyone, and the page
 *   sent home. The page-reload overlay of jitsi-meet, which exists to rejoin
 *   the same room after exactly these errors, never got its turn.
 * - Native already kept the call (the failure reaches it only as
 *   CONFERENCE_LEFT) and rejoins through PageReloadDialog, but cancelling that
 *   dialog left the call running with nobody to end it.
 *
 * Now a media-session failure of the joined call's conference is recorded
 * here, the exit that follows keeps the call, and the existing reload path
 * rejoins it, after MEDIA_REJOIN_RELOAD_SECONDS instead of 10–30 s. On web the
 * record is a tab marker, so it survives the reload, and the client's page
 * exit (leaveClientCallOnPageHide in the middleware) reads it and keeps the
 * call on that reload.
 * Three failures within five minutes are rejoined; the fourth ends the call as
 * before, because this device cannot hold its media. Leaving instead of
 * rejoining ends the call too. Group calls keep their own leave-and-rejoin
 * rules (groupRejoin.ts) and are not handled here.
 */
/**
 * The lib-jitsi-meet JitsiConferenceErrors values that say the media session failed,
 * while the room and the call are fine. This is the set jitsi-meet itself
 * answers with "reload and rejoin" (isFatalJitsiConferenceError). They are
 * raw strings on purpose: importing the SDK enums would drag the whole SDK
 * into every sandboxed harness that loads the queue middleware.
 */
export const MEDIA_SESSION_FAILURES = [
    'conference.focusDisconnected',
    'conference.focusLeft',
    'conference.iceFailed',
    'conference.offerAnswerFailed',
    'conference.videobridgeNotAvailable'
];
/** Tab-scoped sessionStorage on web; memory on native. */
export const MEDIA_REJOIN_KEY = 'vrs_media_rejoin';
/** Rejoins allowed per call within MEDIA_REJOIN_WINDOW_MS. */
export const MEDIA_REJOIN_MAX_ATTEMPTS = 3;
export const MEDIA_REJOIN_WINDOW_MS = 5 * 60 * 1000;
/** A rejoin not back in the room this long after the failure is no longer pending. */
export const MEDIA_REJOIN_PENDING_MS = 2 * 60 * 1000;
/** The reload countdown for a pending rejoin (jitsi-meet's own is 10–30 s). */
export const MEDIA_REJOIN_RELOAD_SECONDS = 3;
/**
 * Whether a conference error name is a media-session failure.
 *
 * @param {unknown} name - The error name.
 * @returns {boolean}
 */
export function isMediaSessionFailure(name) {
    return typeof name === 'string' && MEDIA_SESSION_FAILURES.includes(name);
}
/**
 * A timestamp that is not in the future.
 *
 * @param {unknown} value - The value.
 * @param {number} now - The current time.
 * @returns {boolean}
 */
function isTime(value, now) {
    return typeof value === 'number' && Number.isFinite(value) && value <= now;
}
/**
 * Reads a stored marker. Anything malformed, from the future, or older than
 * the window is no marker at all.
 *
 * @param {string|null|undefined} raw - The stored value.
 * @param {number} now - The current time.
 * @returns {IMediaRejoinMarker|null}
 */
export function parseMediaRejoinMarker(raw, now) {
    let value;
    try {
        value = JSON.parse(raw || 'null');
    }
    catch {
        return null;
    }
    if (!value || typeof value !== 'object') {
        return null;
    }
    const marker = value;
    if (marker.version !== 1
        || typeof marker.callId !== 'string' || !marker.callId
        || typeof marker.roomName !== 'string' || !marker.roomName
        || typeof marker.error !== 'string'
        || typeof marker.pending !== 'boolean'
        || typeof marker.attempts !== 'number' || !Number.isInteger(marker.attempts) || marker.attempts < 1
        || !isTime(marker.firstFailedAt, now) || !isTime(marker.lastFailedAt, now)
        || marker.lastFailedAt < marker.firstFailedAt
        || now - marker.firstFailedAt > MEDIA_REJOIN_WINDOW_MS) {
        return null;
    }
    return {
        attempts: marker.attempts,
        callId: marker.callId,
        error: marker.error,
        firstFailedAt: marker.firstFailedAt,
        lastFailedAt: marker.lastFailedAt,
        pending: marker.pending,
        roomName: marker.roomName,
        version: 1
    };
}
/**
 * Records a media-session failure of a call. A failure of the same call
 * within the window counts as a further attempt.
 *
 * @param {IMediaRejoinMarker|null} previous - The current marker, if any.
 * @param {Object} call - The call whose conference failed.
 * @param {string} error - The conference error name.
 * @param {number} now - The current time.
 * @returns {IMediaRejoinMarker} The new marker; `pending` says whether to rejoin.
 */
export function recordMediaFailure(previous, call, error, now) {
    const sameCall = previous?.callId === call.callId && now - previous.firstFailedAt <= MEDIA_REJOIN_WINDOW_MS;
    const attempts = sameCall && previous ? previous.attempts + 1 : 1;
    return {
        attempts,
        callId: call.callId,
        error,
        firstFailedAt: sameCall && previous ? previous.firstFailedAt : now,
        lastFailedAt: now,
        pending: attempts <= MEDIA_REJOIN_MAX_ATTEMPTS,
        roomName: call.roomName,
        version: 1
    };
}
/**
 * Whether a marker says this call is between a failure and its rejoin.
 *
 * @param {IMediaRejoinMarker|null} marker - The marker.
 * @param {string|undefined} callId - The call.
 * @param {number} now - The current time.
 * @returns {boolean}
 */
export function isMediaRejoinPending(marker, callId, now) {
    return Boolean(marker?.pending && callId && marker.callId === callId
        && now - marker.lastFailedAt <= MEDIA_REJOIN_PENDING_MS);
}
let memoryMarker = null;
/**
 * This tab's sessionStorage, or undefined on native (and where it is blocked).
 *
 * @returns {Storage|undefined}
 */
function tabStorage() {
    try {
        return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
    }
    catch {
        return undefined;
    }
}
/**
 * The marker for this tab (web) or this app process (native).
 *
 * @param {number} now - The current time.
 * @returns {IMediaRejoinMarker|null}
 */
export function readMediaRejoinMarker(now = Date.now()) {
    const storage = tabStorage();
    let raw = memoryMarker;
    if (storage) {
        try {
            raw = storage.getItem(MEDIA_REJOIN_KEY);
        }
        catch { /* storage blocked: the in-memory copy stands */ }
    }
    return parseMediaRejoinMarker(raw, now);
}
/**
 * Saves or removes the marker.
 *
 * @param {IMediaRejoinMarker|null} marker - The marker, or null to remove it.
 * @returns {void}
 */
export function writeMediaRejoinMarker(marker) {
    memoryMarker = marker ? JSON.stringify(marker) : null;
    const storage = tabStorage();
    if (!storage) {
        return;
    }
    try {
        if (memoryMarker) {
            storage.setItem(MEDIA_REJOIN_KEY, memoryMarker);
        }
        else {
            storage.removeItem(MEDIA_REJOIN_KEY);
        }
    }
    catch { /* storage blocked: the in-memory copy stands for this page */ }
}
/**
 * The countdown the reload overlay (web) and dialog (native) use while a
 * rejoin is pending, or undefined for jitsi-meet's own.
 *
 * @param {number} now - The current time.
 * @returns {number|undefined}
 */
export function mediaRejoinReloadSeconds(now = Date.now()) {
    const marker = readMediaRejoinMarker(now);
    return isMediaRejoinPending(marker, marker?.callId, now) ? MEDIA_REJOIN_RELOAD_SECONDS : undefined;
}
/**
 * A short, bounded description of the failure for telemetry: the SDK passes
 * the browser's own error (an SDP or ICE message) as the first parameter.
 * Network addresses are removed; nothing else in these messages is personal.
 *
 * @param {unknown} params - The conference error's params.
 * @returns {string|undefined}
 */
export function mediaFailureMessage(params) {
    const first = Array.isArray(params) ? params[0] : undefined;
    let text;
    if (typeof first === 'string') {
        text = first;
    }
    else if (first && typeof first === 'object') {
        const { message } = first;
        text = typeof message === 'string' ? message : undefined;
    }
    if (!text) {
        return undefined;
    }
    return text
        .replace(/\b\d{1,3}(?:\.\d{1,3}){3}\b/g, '<ip>')
        .replace(/\b(?:[0-9a-f]{1,4})?(?::{1,2}[0-9a-f]{1,4}){2,7}\b/gi, '<ip>')
        .slice(0, 300);
}
